// src/services/bookingService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc,
  getDocs, 
  setDoc, 
  query, 
  where, 
  orderBy,
  limit as limitTo,
  increment,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { notificationService } from './notificationService';
import { firestoreService } from './firestoreService';

class BookingService {
  constructor() {
    this.booksCollection = 'pb-books';
    this.bookingsCollection = 'pb-bookings';
  }

  // ============ BOOKING LISTINGS METHODS ============
  
  async getBookingListings(bookId) {
    try {
      const listingsRef = collection(db, this.booksCollection, bookId, 'listings');
      const q = query(listingsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting booking listings:', error);
      throw error;
    }
  }

  async getBookingListing(bookId, listingId) {
    try {
      const listingRef = doc(db, this.booksCollection, bookId, 'listings', listingId);
      const docSnap = await getDoc(listingRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Listing not found');
      }
    } catch (error) {
      console.error('Error getting booking listing:', error);
      throw error;
    }
  }

  async createBookingListing(bookId, listingData) {
    try {
      const listingsRef = collection(db, this.booksCollection, bookId, 'listings');
      const docRef = await addDoc(listingsRef, {
        ...listingData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        views: 0,
        bookings: 0,
        rating: 0,
        reviewCount: 0
      });
      
      // Update book stats
      await this.updateBookingStats(bookId, { totalListings: increment(1) });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating booking listing:', error);
      throw error;
    }
  }

  async updateBookingListing(bookId, listingId, updates) {
    try {
      const listingRef = doc(db, this.booksCollection, bookId, 'listings', listingId);
      await updateDoc(listingRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating booking listing:', error);
      throw error;
    }
  }

  async deleteBookingListing(bookId, listingId) {
    try {
      const listingRef = doc(db, this.booksCollection, bookId, 'listings', listingId);
      await deleteDoc(listingRef);
      
      // Update book stats
      await this.updateBookingStats(bookId, { totalListings: increment(-1) });
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting booking listing:', error);
      throw error;
    }
  }

  // ============ BOOKING RESERVATIONS METHODS ============
  
  async getBookingReservations(bookId, filters = {}) {
    try {
      const reservationsRef = collection(db, this.booksCollection, bookId, 'reservations');
      let q = query(reservationsRef, orderBy('createdAt', 'desc'));
      
      if (filters.status) {
        q = query(reservationsRef, where('status', '==', filters.status), orderBy('createdAt', 'desc'));
      }
      
      if (filters.listingId) {
        q = query(reservationsRef, where('listingId', '==', filters.listingId), orderBy('createdAt', 'desc'));
      }
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting booking reservations:', error);
      throw error;
    }
  }

  async createBookingReservation(bookId, reservationData) {
    try {
      const reservationsRef = collection(db, this.booksCollection, bookId, 'reservations');
      const docRef = await addDoc(reservationsRef, {
        ...reservationData,
        bookId,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Update booking stats
      await this.updateBookingStats(bookId, { 
        totalBookings: increment(1),
        pendingBookings: increment(1)
      });
      
      // Update listing stats
      if (reservationData.listingId) {
        await this.updateBookingListing(bookId, reservationData.listingId, {
          bookings: increment(1)
        });
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating booking reservation:', error);
      throw error;
    }
  }

  async updateBookingReservation(bookId, reservationId, updates) {
    try {
      const reservationRef = doc(db, this.booksCollection, bookId, 'reservations', reservationId);
      const oldReservation = await getDoc(reservationRef);
      
      await updateDoc(reservationRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Update stats if status changed
      if (oldReservation.exists() && updates.status) {
        const oldStatus = oldReservation.data().status;
        const newStatus = updates.status;
        
        if (oldStatus !== newStatus) {
          const statsUpdate = {};
          
          // Decrement old status
          if (oldStatus === 'pending') statsUpdate.pendingBookings = increment(-1);
          if (oldStatus === 'confirmed') statsUpdate.confirmedBookings = increment(-1);
          if (oldStatus === 'completed') statsUpdate.completedBookings = increment(-1);
          
          // Increment new status
          if (newStatus === 'pending') statsUpdate.pendingBookings = increment(1);
          if (newStatus === 'confirmed') statsUpdate.confirmedBookings = increment(1);
          if (newStatus === 'completed') statsUpdate.completedBookings = increment(1);
          
          await this.updateBookingStats(bookId, statsUpdate);
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating booking reservation:', error);
      throw error;
    }
  }

  // ============ BOOKING VENDORS METHODS (for multi-vendor) ============
  
  async getBookingVendors(bookId) {
    try {
      const vendorsRef = collection(db, this.booksCollection, bookId, 'vendors');
      const q = query(vendorsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting booking vendors:', error);
      throw error;
    }
  }

  async getFeaturedVendors(bookId, limitCount = 8) {
    try {
      const vendorsRef = collection(db, this.booksCollection, bookId, 'vendors');
      const q = query(
        vendorsRef, 
        where('featured', '==', true),
        where('status', '==', 'approved'),
        orderBy('rating', 'desc'),
        limitTo(limitCount)
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting featured vendors:', error);
      throw error;
    }
  }

  // ============ BOOKING REVIEWS METHODS ============
  
  async getBookingReviews(bookId, listingId = null) {
    try {
      const reviewsRef = collection(db, this.booksCollection, bookId, 'reviews');
      let q = query(reviewsRef, orderBy('createdAt', 'desc'));
      
      if (listingId) {
        q = query(reviewsRef, where('listingId', '==', listingId), orderBy('createdAt', 'desc'));
      }
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting booking reviews:', error);
      throw error;
    }
  }

  async createBookingReview(bookId, reviewData) {
    try {
      const reviewsRef = collection(db, this.booksCollection, bookId, 'reviews');
      const docRef = await addDoc(reviewsRef, {
        ...reviewData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Update listing rating
      if (reviewData.listingId) {
        await this.updateListingRating(bookId, reviewData.listingId);
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating booking review:', error);
      throw error;
    }
  }

  // ============ UTILITY METHODS ============
  
  async updateBookingStats(bookId, updates) {
    try {
      const bookRef = doc(db, this.booksCollection, bookId);
      await updateDoc(bookRef, {
        [`bookingStats.${Object.keys(updates)[0]}`]: Object.values(updates)[0],
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating booking stats:', error);
      throw error;
    }
  }

  async updateListingRating(bookId, listingId) {
    try {
      const reviews = await this.getBookingReviews(bookId, listingId);
      
      if (reviews.length > 0) {
        const avgRating = reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length;
        
        await this.updateBookingListing(bookId, listingId, {
          rating: Number(avgRating.toFixed(1)),
          reviewCount: reviews.length
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error updating listing rating:', error);
      throw error;
    }
  }

  // ============ FILE UPLOAD METHODS (using firestoreService) ============
  
  async uploadListingMedia(file, bookId, listingId, mediaType = 'images') {
    try {
      const path = `pb-bookings/${bookId}/listings/${listingId}/${mediaType}`;
      return await firestoreService.uploadFile(file, path);
    } catch (error) {
      console.error('Error uploading listing media:', error);
      throw error;
    }
  }

  async deleteListingMedia(imageUrl) {
    try {
      return await firestoreService.deleteFile(imageUrl);
    } catch (error) {
      console.error('Error deleting listing media:', error);
      throw error;
    }
  }

  // ============ ANALYTICS METHODS ============
  
  async trackListingView(bookId, listingId, userId = null) {
    try {
      // Track view in listing
      await this.updateBookingListing(bookId, listingId, {
        views: increment(1)
      });
      
      // Track in analytics subcollection
      const analyticsRef = collection(db, this.booksCollection, bookId, 'analytics');
      await addDoc(analyticsRef, {
        type: 'listing_view',
        listingId,
        userId: userId || 'anonymous',
        timestamp: serverTimestamp(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server'
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error tracking listing view:', error);
      return { success: false };
    }
  }

  async trackBookingInteraction(bookId, listingId, interactionType, userId = null) {
    try {
      const analyticsRef = collection(db, this.booksCollection, bookId, 'analytics');
      await addDoc(analyticsRef, {
        type: interactionType, // 'contact', 'booking_attempt', 'wishlist_add', etc.
        listingId,
        userId: userId || 'anonymous',
        timestamp: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error tracking booking interaction:', error);
      return { success: false };
    }
  }

  async createBookingRequest(bookId, requestData) {
    try {
      const requestId = doc(collection(db, 'pb-books', bookId, 'requests')).id;
      const requestDoc = {
        ...requestData,
        id: requestId,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'pb-books', bookId, 'requests', requestId), requestDoc);
      return requestId;
    } catch (error) {
      console.error('Error creating booking request:', error);
      throw error;
    }
  }

  async findProvidersInArea(location, serviceType) {
    try {
      // Use the correct collection name and method
      const vendors = await firestoreService.getVendorsByService(serviceType, location);
      return vendors || [];
    } catch (error) {
      console.error('Error finding vendors in area:', error);
      return [];
    }
  }

  async notifyProvidersForRequest(requestData) {
    try {
      const { bookingType, requestType } = requestData;
      
      switch (bookingType) {
        case 'accommodation':
          if (requestData.isLocationFlexible) {
            await this.notifyAllAccommodationProviders(requestData);
          } else {
            await this.notifyProvidersInArea(requestData.preferredProvince, requestData);
          }
          break;
          
        case 'appointment':
          await this.notifyAppointmentProviders(requestData);
          break;
          
        case 'rental':
          await this.notifyRentalProviders(requestData);
          break;
          
        case 'venue':
          await this.notifyVenueProviders(requestData);
          break;
          
        default:
          console.warn('Unknown booking type for notifications:', bookingType);
      }
    } catch (error) {
      console.error('Error notifying providers:', error);
    }
  }

  // Find and notify accommodation providers in specific area
  async notifyProvidersInArea(location, requestData) {
    try {
      // Find providers in the area
      const providers = await this.findProvidersInArea(location, 'accommodation');
      
      // Create notifications for each provider
      const notificationPromises = providers.map(provider => 
        notificationService.createProviderNotification(provider.userId, {
          type: 'booking_request',
          category: 'accommodation_request',
          title: 'New Accommodation Request',
          message: `New ${requestData.accommodation?.accommodationType?.join(', ') || 'accommodation'} request in ${location}`,
          priority: 'normal',
          actionRequired: true,
          
          // Request data for provider to review
          requestData: {
            id: requestData.requestId,
            bookingType: requestData.bookingType,
            location: `${requestData.preferredCity}, ${requestData.preferredProvince}`,
            dates: requestData.isDateFlexible ? 'Flexible dates' : 
              `${requestData.accommodation?.checkIn} to ${requestData.accommodation?.checkOut}`,
            guests: `${requestData.accommodation?.adults} adults, ${requestData.accommodation?.children} children`,
            rooms: requestData.accommodation?.rooms,
            budget: requestData.accommodation?.budget?.flexible ? 'Flexible budget' :
              `R${requestData.accommodation?.budget?.min} - R${requestData.accommodation?.budget?.max}`,
            amenities: requestData.accommodation?.amenities || [],
            specialRequirements: requestData.specialRequirements,
            contactInfo: {
              name: requestData.contactName,
              email: requestData.contactEmail,
              phone: requestData.contactPhone
            }
          },

          // Action buttons for the notification
          actions: [
            {
              label: 'View Details',
              type: 'view',
              url: `/provider/requests/${requestData.requestId}`
            },
            {
              label: 'Send Proposal',
              type: 'respond',
              url: `/provider/proposals/create?requestId=${requestData.requestId}`
            },
            {
              label: 'Not Available',
              type: 'decline',
              action: 'decline_request'
            }
          ]
        })
      );

      await Promise.all(notificationPromises);
      console.log(`Notified ${providers.length} accommodation providers in ${location}`);
      
    } catch (error) {
      console.error('Error notifying providers in area:', error);
    }
  }

  // Find providers in specific area
  async findProvidersInArea(location, serviceType) {
    try {
      // Query providers based on location and service type
      const providers = await firestoreService.getDocuments('pb-providers', {
        where: [
          ['serviceType', '==', serviceType],
          ['location', '==', location],
          ['status', '==', 'active'],
          ['acceptingRequests', '==', true]
        ]
      });

      return providers || [];
    } catch (error) {
      console.error('Error finding providers in area:', error);
      return [];
    }
  }

  // Notify all accommodation providers for flexible requests
  async notifyAllAccommodationProviders(requestData) {
    try {
     
      const vendors = await firestoreService.getVendorsByService('accommodation');

      const notificationPromises = vendors.map(vendor => 
        notificationService.createProviderNotification(vendor.userId, {
          type: 'booking_request',
          category: 'accommodation_request',
          title: 'Flexible Accommodation Request',
          message: 'New flexible accommodation request - location and dates negotiable',
          priority: 'normal',
          actionRequired: true,
          requestData: {
            id: requestData.requestId,
            bookingType: requestData.bookingType,
            flexibility: {
              location: requestData.isLocationFlexible,
              dates: requestData.isDateFlexible
            },
            preferences: requestData.accommodation,
            contactInfo: {
              name: requestData.contactName,
              email: requestData.contactEmail,
              phone: requestData.contactPhone
            }
          },
          actions: [
            {
              label: 'View Details',
              type: 'view',
              url: `/provider/requests/${requestData.requestId}`
            },
            {
              label: 'Send Proposal',
              type: 'respond',
              url: `/provider/proposals/create?requestId=${requestData.requestId}`
            }
          ]
        })
      );

      await Promise.all(notificationPromises);
      console.log(`Notified ${vendors.length} accommodation providers for flexible request`);
      
    } catch (error) {
      console.error('Error notifying all accommodation providers:', error);
    }
  }

  // Notify appointment service providers
  async notifyAppointmentProviders(requestData) {
    try {
      const location = requestData.isLocationFlexible ? 'flexible' : 
        `${requestData.preferredCity}, ${requestData.preferredProvince}`;
      
      const providers = await this.findProvidersInArea(location, 'appointment');
      
      const notificationPromises = providers.map(provider => 
        notificationService.createProviderNotification(provider.userId, {
          type: 'booking_request',
          category: 'appointment_request',
          title: 'New Appointment Request',
          message: `New ${requestData.appointment?.serviceType || 'appointment'} request`,
          priority: 'high',
          actionRequired: true,
          requestData: {
            id: requestData.requestId,
            serviceType: requestData.appointment?.serviceType,
            preferredDate: requestData.appointment?.date,
            timeSlots: requestData.appointment?.timeSlots,
            duration: requestData.appointment?.duration,
            isOnlineAvailable: requestData.appointment?.isOnlineAvailable,
            contactInfo: {
              name: requestData.contactName,
              email: requestData.contactEmail,
              phone: requestData.contactPhone
            }
          },
          actions: [
            {
              label: 'Check Availability',
              type: 'view',
              url: `/provider/appointments/${requestData.requestId}`
            },
            {
              label: 'Book Appointment',
              type: 'respond',
              url: `/provider/appointments/book?requestId=${requestData.requestId}`
            }
          ]
        })
      );

      await Promise.all(notificationPromises);
      console.log(`Notified ${providers.length} appointment providers`);
      
    } catch (error) {
      console.error('Error notifying appointment providers:', error);
    }
  }

  // Notify rental providers
  async notifyRentalProviders(requestData) {
    try {
      const location = requestData.isLocationFlexible ? 'flexible' : 
        `${requestData.preferredCity}, ${requestData.preferredProvince}`;
      
      const providers = await this.findProvidersInArea(location, 'rental');
      
      const notificationPromises = providers.map(provider => 
        notificationService.createProviderNotification(provider.userId, {
          type: 'booking_request',
          category: 'rental_request',
          title: 'New Rental Request',
          message: `New ${requestData.rental?.category || 'rental'} request`,
          priority: 'normal',
          actionRequired: true,
          requestData: {
            id: requestData.requestId,
            category: requestData.rental?.category,
            pickupDate: requestData.rental?.pickupDate,
            returnDate: requestData.rental?.returnDate,
            pickupLocation: requestData.rental?.pickupLocation,
            insurance: requestData.rental?.insurance,
            deliveryRequired: requestData.rental?.deliveryRequired,
            contactInfo: {
              name: requestData.contactName,
              email: requestData.contactEmail,
              phone: requestData.contactPhone
            }
          },
          actions: [
            {
              label: 'Check Availability',
              type: 'view',
              url: `/provider/rentals/${requestData.requestId}`
            },
            {
              label: 'Send Quote',
              type: 'respond',
              url: `/provider/quotes/create?requestId=${requestData.requestId}`
            }
          ]
        })
      );

      await Promise.all(notificationPromises);
      console.log(`Notified ${providers.length} rental providers`);
      
    } catch (error) {
      console.error('Error notifying rental providers:', error);
    }
  }

  // Notify venue providers
  async notifyVenueProviders(requestData) {
    try {
      const location = requestData.isLocationFlexible ? 'flexible' : 
        `${requestData.preferredCity}, ${requestData.preferredProvince}`;
      
      const providers = await this.findProvidersInArea(location, 'venue');
      
      const notificationPromises = providers.map(provider => 
        notificationService.createProviderNotification(provider.userId, {
          type: 'booking_request',
          category: 'venue_request',
          title: 'New Venue Request',
          message: `New ${requestData.venue?.eventType || 'event'} venue request`,
          priority: 'normal',
          actionRequired: true,
          requestData: {
            id: requestData.requestId,
            eventType: requestData.venue?.eventType,
            eventDate: requestData.venue?.eventDate,
            startTime: requestData.venue?.startTime,
            endTime: requestData.venue?.endTime,
            expectedGuests: requestData.venue?.expectedGuests,
            catering: requestData.venue?.catering,
            setup: requestData.venue?.setup,
            equipment: requestData.venue?.equipment,
            contactInfo: {
              name: requestData.contactName,
              email: requestData.contactEmail,
              phone: requestData.contactPhone
            }
          },
          actions: [
            {
              label: 'Check Availability',
              type: 'view',
              url: `/provider/venues/${requestData.requestId}`
            },
            {
              label: 'Send Proposal',
              type: 'respond',
              url: `/provider/proposals/create?requestId=${requestData.requestId}`
            }
          ]
        })
      );

      await Promise.all(notificationPromises);
      console.log(`Notified ${providers.length} venue providers`);
      
    } catch (error) {
      console.error('Error notifying venue providers:', error);
    }
  }

  async getFeaturedListings(bookId, limit = 5) {
    try {
      const now = new Date();
      const currentISOString = now.toISOString();
      
      const listingsQuery = query(
        collection(db, 'pb-books', bookId, 'listings'),
        where('isFeatured', '==', true),
        where('status', '==', 'active'),
        where('featuredStartDate', '<=', currentISOString.split('T')[0]),
        orderBy('featuredStartDate', 'desc'),
        limitTo(limit)
      );
      
      const snapshot = await getDocs(listingsQuery);
      const listings = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('Fetched listings', listings);

      return listings;

    } catch (error) {
      console.error('Error getting featured listings:', error);
      return [];
    }
  }

  async createBooking(bookingData) {
    try {
      const bookingRef = collection(db, this.bookingsCollection);
      const docRef = await addDoc(bookingRef, {
        ...bookingData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        version: 1
      });
      
      console.log('Booking created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw new Error(`Failed to create booking: ${error.message}`);
    }
  }

  /**
   * Create multiple bookings in batch
   * @param {Array} bookingsArray - Array of booking objects
   * @returns {Promise<Array>} - Array of booking IDs
   */
  async createBookingsBatch(bookingsArray) {
    try {
      const batch = writeBatch(db);
      const bookingIds = [];
      
      bookingsArray.forEach(bookingData => {
        const bookingRef = doc(collection(db, this.bookingsCollection));
        batch.set(bookingRef, {
          ...bookingData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          version: 1
        });
        bookingIds.push(bookingRef.id);
      });
      
      await batch.commit();
      console.log('Batch bookings created:', bookingIds);
      return bookingIds;
    } catch (error) {
      console.error('Error creating batch bookings:', error);
      throw new Error(`Failed to create batch bookings: ${error.message}`);
    }
  }

  // ============ READ OPERATIONS ============
  
  /**
   * Get a single booking by ID
   * @param {string} bookingId - Booking ID
   * @returns {Promise<Object|null>} - Booking data or null if not found
   */
  async getBooking(bookingId) {
    try {
      const bookingRef = doc(db, this.bookingsCollection, bookingId);
      const docSnap = await getDoc(bookingRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        console.log('Booking not found:', bookingId);
        return null;
      }
    } catch (error) {
      console.error('Error getting booking:', error);
      throw new Error(`Failed to get booking: ${error.message}`);
    }
  }

  /**
   * Get all bookings with optional filters
   * @param {Object} filters - Filter options
   * @returns {Promise<Array>} - Array of bookings
   */
  async getBookings(filters = {}) {
    try {
      let bookingQuery = collection(db, this.bookingsCollection);
      
      // Apply filters
      if (filters.status) {
        bookingQuery = query(bookingQuery, where('status', '==', filters.status));
      }
      
      if (filters.bookId) {
        bookingQuery = query(bookingQuery, where('bookId', '==', filters.bookId));
      }
      
      if (filters.userId) {
        bookingQuery = query(bookingQuery, where('userId', '==', filters.userId));
      }
      
      if (filters.email) {
        bookingQuery = query(bookingQuery, where('email', '==', filters.email));
      }
      
      if (filters.dateFrom && filters.dateTo) {
        bookingQuery = query(
          bookingQuery,
          where('checkIn', '>=', filters.dateFrom),
          where('checkIn', '<=', filters.dateTo)
        );
      }
      
      // Apply ordering
      const orderField = filters.orderBy || 'createdAt';
      const orderDirection = filters.orderDirection || 'desc';
      bookingQuery = query(bookingQuery, orderBy(orderField, orderDirection));
      
      // Apply limit
      if (filters.limit) {
        bookingQuery = query(bookingQuery, limitTo(filters.limit));
      }
      
      const querySnapshot = await getDocs(bookingQuery);
      const bookings = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`Retrieved ${bookings.length} bookings`);
      return bookings;
    } catch (error) {
      console.error('Error getting bookings:', error);
      throw new Error(`Failed to get bookings: ${error.message}`);
    }
  }

  /**
   * Get bookings by booking reference
   * @param {string} bookingReference - Booking reference
   * @returns {Promise<Object|null>} - Booking data or null
   */
  async getBookingByReference(bookingReference) {
    try {
      const bookingQuery = query(
        collection(db, this.bookingsCollection),
        where('bookingReference', '==', bookingReference),
        limitTo(1)
      );
      
      const querySnapshot = await getDocs(bookingQuery);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      } else {
        console.log('Booking not found with reference:', bookingReference);
        return null;
      }
    } catch (error) {
      console.error('Error getting booking by reference:', error);
      throw new Error(`Failed to get booking by reference: ${error.message}`);
    }
  }

  /**
   * Get user's bookings
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} - Array of user's bookings
   */
  async getUserBookings(userId, options = {}) {
    try {
      let bookingQuery = query(
        collection(db, this.bookingsCollection),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      if (options.limit) {
        bookingQuery = query(bookingQuery, limitTo(options.limit));
      }
      
      const querySnapshot = await getDocs(bookingQuery);
      const bookings = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`Retrieved ${bookings.length} bookings for user:`, userId);
      return bookings;
    } catch (error) {
      console.error('Error getting user bookings:', error);
      throw new Error(`Failed to get user bookings: ${error.message}`);
    }
  }

  // ============ UPDATE OPERATIONS ============
  
  /**
   * Update a booking
   * @param {string} bookingId - Booking ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<boolean>} - Success status
   */
  async updateBooking(bookingId, updateData) {
    try {
      const bookingRef = doc(db, this.bookingsCollection, bookingId);
      
      // Check if booking exists
      const docSnap = await getDoc(bookingRef);
      if (!docSnap.exists()) {
        throw new Error('Booking not found');
      }
      
      const currentVersion = docSnap.data().version || 1;
      
      await updateDoc(bookingRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
        version: currentVersion + 1
      });
      
      console.log('Booking updated:', bookingId);
      return true;
    } catch (error) {
      console.error('Error updating booking:', error);
      throw new Error(`Failed to update booking: ${error.message}`);
    }
  }

  /**
   * Update booking status
   * @param {string} bookingId - Booking ID
   * @param {string} status - New status
   * @param {string} statusReason - Reason for status change (optional)
   * @returns {Promise<boolean>} - Success status
   */
  async updateBookingStatus(bookingId, status, statusReason = '') {
    try {
      const updateData = {
        status,
        statusUpdatedAt: serverTimestamp()
      };
      
      if (statusReason) {
        updateData.statusReason = statusReason;
      }
      
      // Add status-specific fields
      switch (status) {
        case 'confirmed':
          updateData.confirmedAt = serverTimestamp();
          break;
        case 'cancelled':
          updateData.cancelledAt = serverTimestamp();
          break;
        case 'completed':
          updateData.completedAt = serverTimestamp();
          break;
        case 'no_show':
          updateData.noShowAt = serverTimestamp();
          break;
      }
      
      await this.updateBooking(bookingId, updateData);
      console.log('Booking status updated:', bookingId, status);
      return true;
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw new Error(`Failed to update booking status: ${error.message}`);
    }
  }

  /**
   * Update payment status
   * @param {string} bookingId - Booking ID
   * @param {string} paymentStatus - New payment status
   * @param {Object} paymentDetails - Payment details (optional)
   * @returns {Promise<boolean>} - Success status
   */
  async updatePaymentStatus(bookingId, paymentStatus, paymentDetails = {}) {
    try {
      const updateData = {
        paymentStatus,
        paymentUpdatedAt: serverTimestamp(),
        ...paymentDetails
      };
      
      if (paymentStatus === 'paid') {
        updateData.paidAt = serverTimestamp();
      }
      
      await this.updateBooking(bookingId, updateData);
      console.log('Payment status updated:', bookingId, paymentStatus);
      return true;
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw new Error(`Failed to update payment status: ${error.message}`);
    }
  }

  // ============ DELETE OPERATIONS ============
  
  /**
   * Delete a booking (soft delete by default)
   * @param {string} bookingId - Booking ID
   * @param {boolean} hardDelete - Whether to permanently delete
   * @returns {Promise<boolean>} - Success status
   */
  async deleteBooking(bookingId, hardDelete = false) {
    try {
      const bookingRef = doc(db, this.bookingsCollection, bookingId);
      
      // Check if booking exists
      const docSnap = await getDoc(bookingRef);
      if (!docSnap.exists()) {
        throw new Error('Booking not found');
      }
      
      if (hardDelete) {
        await deleteDoc(bookingRef);
        console.log('Booking permanently deleted:', bookingId);
      } else {
        // Soft delete
        await updateDoc(bookingRef, {
          deleted: true,
          deletedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        console.log('Booking soft deleted:', bookingId);
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw new Error(`Failed to delete booking: ${error.message}`);
    }
  }

  /**
   * Cancel a booking
   * @param {string} bookingId - Booking ID
   * @param {string} reason - Cancellation reason
   * @param {string} cancelledBy - Who cancelled (guest/host/admin)
   * @returns {Promise<boolean>} - Success status
   */
  async cancelBooking(bookingId, reason = '', cancelledBy = 'guest') {
    try {
      const updateData = {
        status: 'cancelled',
        cancellationReason: reason,
        cancelledBy,
        cancelledAt: serverTimestamp()
      };
      
      await this.updateBooking(bookingId, updateData);
      console.log('Booking cancelled:', bookingId);
      return true;
    } catch (error) {
      console.error('Error cancelling booking:', error);
      throw new Error(`Failed to cancel booking: ${error.message}`);
    }
  }

  // ============ UTILITY METHODS ============
  
  /**
   * Get booking statistics
   * @param {Object} filters - Filter options
   * @returns {Promise<Object>} - Booking statistics
   */
  async getBookingStats(filters = {}) {
    try {
      const bookings = await this.getBookings(filters);
      
      const stats = {
        total: bookings.length,
        confirmed: bookings.filter(b => b.status === 'confirmed').length,
        pending: bookings.filter(b => b.status === 'pending').length,
        cancelled: bookings.filter(b => b.status === 'cancelled').length,
        completed: bookings.filter(b => b.status === 'completed').length,
        totalRevenue: bookings
          .filter(b => b.status === 'confirmed' || b.status === 'completed')
          .reduce((sum, b) => sum + (b.totalPrice || 0), 0)
      };
      
      console.log('Booking stats:', stats);
      return stats;
    } catch (error) {
      console.error('Error getting booking stats:', error);
      throw new Error(`Failed to get booking stats: ${error.message}`);
    }
  }

  /**
   * Search bookings
   * @param {string} searchTerm - Search term
   * @param {Object} filters - Additional filters
   * @returns {Promise<Array>} - Array of matching bookings
   */
  async searchBookings(searchTerm, filters = {}) {
    try {
      // Get all bookings first (Firestore doesn't support full-text search)
      const allBookings = await this.getBookings(filters);
      
      // Filter by search term
      const searchLower = searchTerm.toLowerCase();
      const matchingBookings = allBookings.filter(booking => {
        return (
          booking.bookingReference?.toLowerCase().includes(searchLower) ||
          booking.firstName?.toLowerCase().includes(searchLower) ||
          booking.lastName?.toLowerCase().includes(searchLower) ||
          booking.email?.toLowerCase().includes(searchLower) ||
          booking.phone?.includes(searchTerm) ||
          booking.placeName?.toLowerCase().includes(searchLower)
        );
      });
      
      console.log(`Found ${matchingBookings.length} bookings matching "${searchTerm}"`);
      return matchingBookings;
    } catch (error) {
      console.error('Error searching bookings:', error);
      throw new Error(`Failed to search bookings: ${error.message}`);
    }
  }
}

export const bookingService = new BookingService();